import { Dispatch, useCallback, useEffect, useReducer, useRef } from 'react';

import { TemplateAction, templateReducer, TemplateState } from './useDesignerLogic';

export type MiddlewareFn = (action: TemplateAction, state: TemplateState) => void;

export const useReducerWithMiddleware = (
  reducer: typeof templateReducer,
  initialState: TemplateState,
  middlewareFns: MiddlewareFn[],
  afterwareFns: MiddlewareFn[],
): [TemplateState, Dispatch<TemplateAction>] => {
  const [state, dispatch] = useReducer(reducer, initialState, undefined);

  const aRef = useRef<TemplateAction | null>();

  const dispatchWithMiddleware: Dispatch<TemplateAction> = useCallback(
    (action: TemplateAction) => {
      middlewareFns.forEach(middlewareFn => middlewareFn(action, state));

      aRef.current = action;

      dispatch(action);
    },
    [middlewareFns, state],
  );

  useEffect(() => {
    if (!aRef.current) return;

    afterwareFns.forEach(afterwareFn => {
      if (!aRef.current) return;
      afterwareFn(aRef.current, state);
    });

    aRef.current = null;
  }, [afterwareFns, state]);

  return [state, dispatchWithMiddleware];
};
